import { Component, OnInit } from "@angular/core";
import { IAggregateFilterEvent } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortType } from "@app/enum/SortType";
import { IFilterOptions } from "@app/interfaces/filters/IFilterOptions";
import { ColumnDataType } from "@app/main/components/reports/enums/ColumnDataType";
import { IDashboardWidget } from "@app/main/modules/dashboard/_models/IDashboardWidget";
import { DashboardNavigationService } from "@app/main/modules/dashboard/dashboard-navigation.service";
import { DateService } from "@app/services/date.service";
import { StateParams } from "@uirouter/angularjs";
import { IDashboard } from "../_models/IDashboard";
import { IPooledRequest } from "../_models/IPooledRequest";
import { IWidgetEditorConfig } from "../_models/IWidgetEditorConfig";
import { WidgetName } from "../_models/widget-name";
import { DashboardPoolingService } from "../dashboard-pooling.service";
import "./dashboard.component.scss";
import { ResourceType } from "@app/enum/ResourceType";
import { TimeFilterType } from "@app/main/components/reports/enums/TimeFilterType";
import { DashboardService } from "../dashboard.service";

@Component({
    selector: "dashboardv3",
    template: require("./dashboard.component.html")
})
export class DashboardComponent implements OnInit {
    loading = false;

    dashboard: IDashboard;

    selectedWidget: IDashboardWidget;

    initialDateRange = DateRange.Last7Days;

    filterOptions: IFilterOptions = {};

    datePeriodString: string;

    editing = false;

    showFilterBar: boolean = true;

    constructor(
        private readonly dashboardPoolingService: DashboardPoolingService,
        private readonly dateService: DateService,
        private readonly $stateParams: StateParams,
        private readonly dashboardNavigationService: DashboardNavigationService,
        private readonly dashboardService: DashboardService
    ) {
    }

    async ngOnInit(): Promise<void> {
        const dashboards = await this.dashboardNavigationService.getNavItems();
        this.dashboard = dashboards.find(x => x.id === this.$stateParams['id']).dashboard;

        this.dashboardService.setShowFilterBar(this.showFilterBar);

        this.requestData();
    }

    onFilterBarChange(event: IAggregateFilterEvent<any>) {

        // We sometimes get weird events come through, need to understand why, until then this fixes a bug
        // This is LIKELY because of the default (change) subscription?
        if (!event.trigger || event.trigger == "custom" || event.trigger == "date" || this.loading) {
            return;
        }

        this.filterOptions.dateFilter = {
            start: event.datetime.startDate,
            end: event.datetime.endDate,
            startTime: event.datetime.startTime,
            endTime: event.datetime.endTime,
            filterType: TimeFilterType.Range
        }

        if (event.group) {
            this.filterOptions.groupFilter = { group: event.group, resourceType: ResourceType.Group };
        } else {
            this.filterOptions.groupFilter = null;
        }

        if (event.driver) {
            this.filterOptions.driverFilter = { driver: event.driver };
        } else {
            this.filterOptions.driverFilter = null;
        }

        if (event.vehicle) {
            this.filterOptions.vehicleFilter = { vehicle: event.vehicle };
        } else {
            this.filterOptions.vehicleFilter = null;
        }

        this.datePeriodString = this.dateService.formatDateRange(this.filterOptions.dateFilter.start, this.filterOptions.dateFilter.end);
        this.requestData();
    }

    setSelectedWidget(widget: IDashboardWidget): void {
        if (!!widget.dataDefinition.Parameters) {
            widget.dataDefinition.Parameters.Start = this.filterOptions.dateFilter.start;
            widget.dataDefinition.Parameters.End = this.filterOptions.dateFilter.end;
        }
        
        this.selectedWidget = widget;
    }

    clearSelectedWidget(): void {
        this.selectedWidget = null;
    }

    //--

    private requestData() {

        if (!this.dashboard?.Widgets) {
            return;
        }

        const widgetsToRequestDataFor = this.dashboard.Widgets.filter(w => w.DataWidgetParameters);
        this.loading = true;

        const pooledRequestUpdated = (pooledRequest: IPooledRequest) => {

            if (pooledRequest.completed) {
                pooledRequest.widgets.forEach(widget => {                  
                    if (widget.onDataDefinitionUpdated) {
                        widget.onDataDefinitionUpdated();
                    }

                    widget.editorConfig = widget.editorConfig || this.createDefaultEditorConfig(widget.WidgetName);
                    const numDataRows = widget?.dataDefinition?.TableDefinition?.Rows?.length;
                    widget.noData = (numDataRows == 0 && numDataRows != widget.editorConfig.numDataSources);
                    widget.processFailed = pooledRequest.failed && widget.DataWidgetParameters.DataSource.length > 0;
                });
            }
        };

        this.dashboardPoolingService.poolRequests(widgetsToRequestDataFor, this.filterOptions, pooledRequestUpdated).then(() => {
            this.updateLoadingIndicator();
        });
    }

    private updateLoadingIndicator() {

        if (!this.dashboard.Widgets) {
            return;
        }

        this.loading = this.dashboard.Widgets.filter(w => w.loading).length > 2;

        this.dashboard.Widgets.forEach(w => {
            w.showLoadingSpinner = w.loading && this.loading != w.loading;
        });
    }

    private createDefaultEditorConfig(widgetName: WidgetName): IWidgetEditorConfig {
        switch (widgetName) {
            case "metric-widget":
                return {
                    numDataSources: 1,
                    filterSortType: SortType.Number,
                    hasCustomEditor: true
                };

            case "gauge-widget":
                return {
                    numDataSources: 1,
                    filterColumnDataType: ColumnDataType.Score
                }

            case "chart-widget":
                return {
                    numDataSources: 5,
                    filterSortType: SortType.Number,
                    hasCustomEditor: true
                };

            case "table-widget":
                return {
                    numDataSources: 5,
                    hasCustomEditor: true
                };

            case "map-widget":
                return {
                    numDataSources: 0,
                    hasCustomEditor: true
                };

            default:
                return {
                    numDataSources: 0
                };
        }
    }
}
